import {
  Flex,
  useTheme,
  Box,
  HStack,
  Editable,
  EditableInput,
  EditablePreview,
  Input,
  Button,
  Spinner,
} from '@chakra-ui/react';
import React, {useEffect, useMemo, useState} from 'react';
import {H2, TextG10, TextG12, TextG14} from '../../../components/typography';
import useGetUserLocation from '../../../hooks/useUserLocation';
import DotSeparator from '../../../components/DotSeparator';
import MeetupRSVPButton from './MeetupRSVPButton';
import IconsHeader from './IconHeader';
import {getDistanceString, calculateDistance} from '../../../utils/distance';
import {getEventDate} from '../../../utils/date';
import UserAvatar from '../../../components/UserAvatar';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useFetchEventRSVPStats} from '../../../hooks/useEvent';
import EditableControls from '../../../components/EditableControls';
import {useCancelMeetup, useUpdateMeetUp} from '../../../hooks/useMeetUps';
import {WhiteButton} from '../../../components/button';
import Icon from '../../../components/Icon';
import MeetupBroadcastButton from './MeetupBroadcastButton';
import PlaceSelectModal from '../../CreateMeetup/PlaceSelectModal';
import EventSelectModal from '../../EventSelectModal';
import {Place, Event} from '../../../types/article';
import {SpinnerLoader} from '../../../components/Loader';
import {EventChatIsClosed} from '../../../components/Chat/EventChat';
import DateTimeEditor from './DateTimeEditor';
import useCountdown from '../../../hooks/useCountdown';
import useCheckIn from '../../../hooks/useCheckIn';
import {
  useRelatedEvents,
  useAddRelatedEvents,
  useRemoveRelatedEvents,
} from '../../../hooks/useEvents';
import {ToastMessage} from '../../../components/toast';
import {useQueryClient} from '@tanstack/react-query';
import RequiresEarlyAccess from '../../../components/RequiresEarlyAccess';
import PromptModal from "../../../components/Modals/PromptModal";
import EventInterestedGoingDisplay from '../../../components/UsersList/EventInterestedGoingDisplay';

const Header: React.FC<{event; isHost}> = ({event, isHost}) => {
  const theme = useTheme();
  const userLocation = useGetUserLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {data} = useFetchEventRSVPStats(event.id || searchParams.get('id'));
  const rsvp_stats = data?.data.rsvp_stats;
  const usersGoing = rsvp_stats?.GOING;
  const interestedAndGoing = (rsvp_stats?.INTERESTED || 0) + (rsvp_stats?.GOING || 0);
  const numOfHearts = event.number_of_hearts;
  const {mutateAsync} = useUpdateMeetUp();
  const [broadModalOpen, setBroadcastModalOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Place>(event.place);
  const [showPlaceModal, setShowPlaceModal] = useState(false);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [placeName, setPlaceName] = useState(event.place?.name);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEventForLink, setSelectedEventForLink] = useState<Event>();
  const [isLinkingEvent, setIsLinkingEvent] = useState(false);
  const {mutate: cancelMeetup} = useCancelMeetup();

  const {data: relatedEventsData, isLoading: relatedEventsLoading} = useRelatedEvents(event.id);
  const relatedEvents = relatedEventsData?.data.data || [];

  const queryClient = useQueryClient();
  const {mutateAsync: addRelatedEvents} = useAddRelatedEvents();
  const {mutateAsync: removeRelatedEvents} = useRemoveRelatedEvents();

  useEffect(() => {
    if (relatedEvents && relatedEvents[0] && relatedEvents[0] !== selectedEventForLink) {
      setSelectedEventForLink(relatedEvents[0]);
    }
  }, [relatedEvents]);

  // Handle selecting a new event for the meetup
  const handleSelectEvent = async (eventToLink: Event) => {
    if (isLinkingEvent) return;

    const previousEvent = selectedEventForLink;
    setIsLinkingEvent(true);

    try {
      setSelectedEventForLink(eventToLink);

      // Check if the selected event has a place that's different from the current meetup place
      if (eventToLink.place && (!event.place || eventToLink.place.id !== event.place.id)) {
        // Update the selected place to match the event's place
        setSelectedPlace(eventToLink.place);
        setPlaceName(eventToLink.place.name);
      }

      if (relatedEvents.length > 0) {
        // Remove existing relationship first
        await removeRelatedEvents({
          eventId: event.id,
          relatedEventId: relatedEvents[0].id,
        });
      }

      // Add new relationship
      await addRelatedEvents({
        eventId: event.id,
        relatedEventId: eventToLink.id,
      });

      // Refresh data
      await queryClient.invalidateQueries(['related-events', event.id]);
      await queryClient.refetchQueries(['related-events', event.id]);

      ToastMessage({
        status: 'success',
        text: 'Event successfully linked to meetup',
      });
    } catch (error) {
      setSelectedEventForLink(previousEvent);
      ToastMessage({
        status: 'error',
        text: 'Failed to link event to meetup',
      });
      await queryClient.refetchQueries(['related-events', event.id]);
    } finally {
      setIsLinkingEvent(false);
    }
  };

  const handleRemoveEvent = async (eventToRemove: Event) => {
    if (isLinkingEvent) return;

    const previousEvent = selectedEventForLink;
    setIsLinkingEvent(true);

    try {
      setSelectedEventForLink(undefined);
      await removeRelatedEvents({
        eventId: event.id,
        relatedEventId: eventToRemove.id,
      });

      ToastMessage({
        status: 'success',
        text: 'Event successfully unlinked from meetup',
      });

      queryClient.invalidateQueries(['related-events', event.id]);
    } catch (error) {
      setSelectedEventForLink(previousEvent);
      ToastMessage({
        status: 'error',
        text: 'Failed to unlink event from meetup',
      });
    } finally {
      setIsLinkingEvent(false);
    }
  };

  const {startPassed, endPassed, canCheckIn} = useCountdown(event);
  const {eventCheckIn} = useCheckIn();
  const action = useMemo(() => searchParams.get('action'), [searchParams]);
  const isLoading = userLocation.isLoading;
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleCancel = () => {
    cancelMeetup(event.id, {
      onSuccess: () => {
        ToastMessage({
          status: 'success',
          text: 'Meetup cancelled successfully',
        });
        setCancelModalOpen(false);
        navigate('/meet-up');
      }
    });
  };


  const handleCheckIn = ({ sdv, qr_code_id }) => {
    if (event.has_checked_in) {
      return;
    }

    if ((!userLocation.data?.latitude || !userLocation.data?.longitude) && !sdv) {
      ToastMessage({
        status: 'error',
        text: 'Unable to get your location. Please enable location services to check-in',
      });
      return;
    }

    eventCheckIn({
      event: event,
      eventOffers: [],
      activeRedemptions: [],
      latitude: userLocation?.data?.latitude,
      longitude: userLocation?.data?.longitude,
      isPublic: true,
      sdv: sdv,
      qr_code_id,
    });
  };

  useEffect(() => {
    if (!userLocation.isLoading && action && action == 'check-in') {
      const qr_code_id_string = searchParams.get('qr_code_id');
      let qr_code_id;
      if (qr_code_id_string) {
        qr_code_id = parseInt(qr_code_id_string);
      }
      handleCheckIn({sdv: !!searchParams.get('sdv'), qr_code_id});
    }
  }, [action, isLoading]);

  useEffect(() => {
    if (selectedPlace && selectedPlace !== event.place) {
      const updatePlace = async () => {
        setPlaceLoading(true);
        setPlaceName(selectedPlace?.name);
        try {
          await mutateAsync({
            ...event,
            title: event.title,
            place_id: selectedPlace.id,
            eventId: event.id,
          });
        } finally {
          setPlaceLoading(false);
        }
      };

      updatePlace();
    }
  }, [selectedPlace]);

  const eventDate = getEventDate(event.start_date_time, event.end_date_time);
  const eventChatClosed = EventChatIsClosed(event);

  return (
    <Flex
      position="relative"
      top="0"
      width="90%"
      marginLeft="5%"
      transform="auto"
      translateY="-35%"
      marginBottom={isHost ? '-10rem' : '-8rem'}
      backgroundColor="white"
      boxShadow={`0 0 12px ${theme.colors.grey}`}
      borderRadius=".5rem"
      p="0.75rem"
      flexDirection="column"
      gap="12px"
      pb="calc(1.25rem + 48px)">
      <Flex direction="column" gap="8px">
        {isHost ? (
          <DateTimeEditor event={event} />
        ) : (
          <TextG10
            fontWeight="500"
            color={theme.colors.grey}
            maxW="150px"
            isTruncated
            noOfLines={1}>
            {eventDate}
          </TextG10>
        )}

        {isHost && !eventChatClosed ? (
          <Editable
            mt={2}
            onSubmit={(value) => {
              mutateAsync({
                ...event,
                title: value,
                place_id: event.place?.id,
                eventId: event.id,
              });
            }}
            pos="relative"
            w="100%"
            fontSize="24px"
            lineHeight="28px"
            fontFamily="Aileron"
            fontWeight="900"
            defaultValue={event.title}
            isPreviewFocusable={false}
            selectAllOnFocus={false}>
            <EditablePreview py="0" />
            <EditableInput />
            <EditableControls top="0px" />
          </Editable>
        ) : (
          <H2>{event.title}</H2>
        )}
      </Flex>
      {event.place && (
        <Flex direction="row" align="center">
          {isHost ? (
            placeLoading ? (
              <Flex w="100%" justify="center">
                <SpinnerLoader />
              </Flex>
            ) : (
              <Flex w="100%" justifyContent="space-between">
                <TextG12
                  onClick={() => navigate(`/place/${event.place.id}`)}
                  color={theme.colors.blue}
                  width="max-content">
                  {placeName}
                </TextG12>
                {!eventChatClosed && (
                  <Button px={4} h={6} minH={6}>
                    <TextG12 onClick={() => setShowPlaceModal(true)}>Edit</TextG12>
                    <PlaceSelectModal
                      showModal={showPlaceModal}
                      setShowModal={setShowPlaceModal}
                      showButton={false}
                      selectedPlace={selectedPlace}
                      setSelectedPlace={setSelectedPlace}
                    />
                  </Button>
                )}
              </Flex>
            )
          ) : (
            <TextG12
              onClick={() => navigate(`/place/${event.place.id}`)}
              color={theme.colors.blue}
              width="max-content">
              {event.place.name}
            </TextG12>
          )}

          {userLocation.data?.latitude &&
            userLocation.data.longitude &&
            event.place?.latitude &&
            event.place?.longitude && (
              <>
                <DotSeparator width="3px" height="3px" color={theme.colors.grey} />
                <TextG12 color={theme.colors.grey} whiteSpace="nowrap">
                  {getDistanceString(
                    calculateDistance(
                      {
                        latitude: userLocation.data?.latitude,
                        longitude: userLocation.data?.longitude,
                      },
                      {
                        latitude: Number(event.place.latitude),
                        longitude: Number(event.place.longitude),
                      }
                    )
                  )}
                </TextG12>
              </>
            )}
        </Flex>
      )}
      {selectedEventForLink && (
        <Flex direction="row" align="center">
          {isHost ? (
            isLinkingEvent ? (
              <Flex w="100%" justify="center">
                <SpinnerLoader />
              </Flex>
            ) : (
              <Flex w="100%" justifyContent="space-between">
                <TextG12
                  onClick={() => navigate(`/event/${selectedEventForLink.id}`)}
                  color={theme.colors.blue}
                  width="max-content">
                  {selectedEventForLink.title || selectedEventForLink.name}
                </TextG12>
                {!eventChatClosed && (
                  <Button px={4} h={6} minH={6}>
                    <TextG12 onClick={() => setShowEventModal(true)}>Edit</TextG12>
                  </Button>
                )}
              </Flex>
            )
          ) : selectedEventForLink ? (
            <TextG12
              onClick={() => navigate(`/event/${selectedEventForLink.id}`)}
              color={theme.colors.blue}
              width="max-content">
              {selectedEventForLink.title || selectedEventForLink.name}
            </TextG12>
          ) : null}
        </Flex>
      )}
      {relatedEventsLoading ? (
        <Flex w="100%" justify="center" py={2}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor={theme.colors.lightGrey}
            color={theme.colors.lightBlue}
            size="sm"
          />
        </Flex>
      ) : (
        isHost &&
        !selectedEventForLink &&
        !eventChatClosed && (
          <Button
            onClick={() => setShowEventModal(true)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="fit-content"
            px={4}
            h={8}
            background="transparent"
            border="1px dashed"
            borderColor="gray.300"
            borderRadius="md">
            <TextG12 color={theme.colors.grey}>+ Add Related Event</TextG12>
          </Button>
        )
      )}
      {interestedAndGoing > 0 && (
        <EventInterestedGoingDisplay eventId={Number(event.id)} hostUserId={event.user_id || event.user?.id} />
      )}
      {numOfHearts > 0 && (
        <TextG12 whiteSpace="nowrap" color={theme.colors.grey}>{`❤️ ${
          numOfHearts > 1 ? `${numOfHearts} People` : '1 Person'
        } Appreciated This`}</TextG12>
      )}
      {!isHost && (
        <WhiteButton
          id="checkin-tooltip"
          onClick={handleCheckIn}
          w="100%"
          flexGrow={1}
          borderRadius={'2.5rem'}
          _hover={{background: ''}}
          color={!canCheckIn ? theme.colors.lightestGrey : theme.colors.white}
          background={
            !canCheckIn
              ? theme.colors.lighterGrey
              : event.has_checked_in
              ? 'green'
              : theme.colors.blue
          }
          isDisabled={endPassed}>
          {event.has_checked_in ? (
            <Flex gap="6px" style={{color: 'white'}}>
              <Icon iconName="fi-sr-check" style={{marginTop: '2px'}} />
              <TextG12 fontWeight="500">Checked-In</TextG12>
            </Flex>
          ) : (
            <TextG12 fontWeight="500">Check-In</TextG12>
          )}
        </WhiteButton>
      )}
      {!isHost && <MeetupRSVPButton event={event} />}
      {isHost && (
        <RequiresEarlyAccess>
          <MeetupBroadcastButton event={event} />
        </RequiresEarlyAccess>
      )}{' '}
      <EventSelectModal
        showModal={showEventModal}
        setShowModal={setShowEventModal}
        showButton={false}
        selectedEvent={selectedEventForLink}
        onSelectEvent={handleSelectEvent}
        onRemoveEvent={handleRemoveEvent}
        modalTitle="Edit Linked Event"
        isLoading={isLinkingEvent}
        closeOnSelect={false}
        placeId={event.place?.id} // Filter events by the meetup's place
      />
      <IconsHeader event={event} isHost={isHost} />
      {isHost && (
        <>
          <WhiteButton
            onClick={() => setCancelModalOpen(true)}
            w="100%"
            border="1px solid"
            borderColor="red"
            color="red"
            borderRadius="2.5rem"
            background="transparent"
            _hover={{bg: theme.colors.lightestGrey}}>
            Cancel Meet-Up
          </WhiteButton>

          <PromptModal
            buttonWidth="fit-content"
            isOpen={cancelModalOpen}
            onClose={() => setCancelModalOpen(false)}
            title="Are you sure you want to cancel this Meet-Up?"
            declineText="No"
            acceptText="Yes, cancel my Meet-Up"
            declineCallback={() => setCancelModalOpen(false)}
            acceptCallback={handleCancel}
            style={{
              ModalContent: {
                maxW: '400px',
              },
            }}
          />
        </>
      )}
      <Box
        display="flex"
        justifyContent="center"
        borderBottomRadius=".5rem"
        w="100%"
        left={0}
        bottom={0}
        pos="absolute"
        py="10px"
        background={theme.colors.lightBlack}>
        <HStack>
          <UserAvatar size="xs" user={event?.user} />
          <TextG12 fontWeight="500" color="white">
            {isHost
              ? `You’re hosting this Meet-Up`
              : `${event.user?.handle} is hosting this Meet-Up`}
          </TextG12>
        </HStack>
      </Box>
    </Flex>
  );
};

export default Header;
